import React, { useContext, useEffect, useState } from 'react'
import { useCurrentUser } from 'lib/CurrentUserProvider';
import { ClientRecord, ContractRecord, VendorRecord } from 'lib/./DataModel';
import { useOrbit, GetRemoteIdFn } from 'providers/OrbitProvider';
import { useParams } from 'react-router-dom';
import MemorySource from '@orbit/memory';

const ScopeContext = React.createContext<{
  currentClient: null | ClientRecord,
  currentVendor: null | VendorRecord,
  currentContract: null | ContractRecord,
  contractsForVendor: ContractRecord[],
  setCurrentClient: React.Dispatch<React.SetStateAction<ClientRecord>>,
  setCurrentVendor: React.Dispatch<React.SetStateAction<VendorRecord>>,
  setCurrentContract: React.Dispatch<React.SetStateAction<ContractRecord>>,
}>({
  currentClient: null,
  currentVendor: null,
  currentContract: null,
  contractsForVendor: [],
  setCurrentClient: () => {return},
  setCurrentVendor: () => {return},
  setCurrentContract: () => {return},
});

function getInitialClient({ getRemoteId, accessibleClients, clientIdFromRoute }: {
  getRemoteId: GetRemoteIdFn,
  accessibleClients: ClientRecord[],
  clientIdFromRoute: string | undefined,
}): ClientRecord | null {
  return accessibleClients.find(c => getRemoteId(c) === clientIdFromRoute) ?? accessibleClients[0] ?? null
}

function getInitialVendor({ getRemoteId, accessibleVendors, vendorIdFromRoute }: {
  getRemoteId: GetRemoteIdFn,
  accessibleVendors: VendorRecord[],
  vendorIdFromRoute: string | undefined,
}): VendorRecord | null {
  return accessibleVendors.find(v => getRemoteId(v) === vendorIdFromRoute) ?? accessibleVendors[0] ?? null
}

function getInitialContract({ store, getRemoteId, accessibleVendorContracts, contractIdFromRoute, currentVendor }: {
  store: MemorySource,
  getRemoteId: GetRemoteIdFn,
  accessibleVendorContracts: ContractRecord[],
  contractIdFromRoute: string | undefined,
  currentVendor: VendorRecord | null,
}): ContractRecord | null {
  return accessibleVendorContracts.find(v => getRemoteId(v) === contractIdFromRoute) ?? getContractsForVendor({ store, currentVendor, accessibleVendorContracts })?.[0] ?? null
}

function getContractsForVendor({ store, currentVendor, accessibleVendorContracts }: {
  store: MemorySource,
  currentVendor: VendorRecord | null,
  accessibleVendorContracts: ContractRecord[]
}): ContractRecord[] {
  if(currentVendor === null) {
    return []
  }

  return accessibleVendorContracts.filter(contract => (store.cache.query<VendorRecord>(q => q.findRelatedRecord(contract, 'vendor')) as VendorRecord)?.id === currentVendor.id)
}

export default function ScopeProvider({ children }) {
  const { accessibleClients, accessibleVendors, accessibleVendorContracts } = useCurrentUser()
  const { store, getRemoteId } = useOrbit()
  const { clientId: clientIdFromRoute, vendorId: vendorIdFromRoute, contractId: contractIdFromRoute } = useParams()
  const [currentClient, setCurrentClient] = useState(() => getInitialClient({ getRemoteId, accessibleClients, clientIdFromRoute }))
  const [currentVendor, setCurrentVendor] = useState(getInitialVendor({ getRemoteId, accessibleVendors, vendorIdFromRoute }))
  const [currentContract, setCurrentContract] = useState(getInitialContract({ getRemoteId, store, accessibleVendorContracts, contractIdFromRoute, currentVendor }))

  const contractsForVendor = getContractsForVendor({ store, currentVendor, accessibleVendorContracts })

  useEffect(() => {
    if(currentContract && contractsForVendor.length > 0 && !contractsForVendor.includes(currentContract)) {
      setCurrentContract(contractsForVendor[0])
    }
  }, [contractsForVendor, currentContract])

  return (
    <ScopeContext.Provider value={{ currentClient, setCurrentClient, currentVendor, setCurrentVendor, currentContract, setCurrentContract, contractsForVendor }}>
      {children}
    </ScopeContext.Provider>
  )
}

export function useScope() {
  return useContext(ScopeContext)
}
